import { IonCol, IonGrid, IonRow } from '@ionic/react';
import equal from 'deep-equal';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import styles from '@components/ArticleFilters.module.css';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Select from '@components/fields/Select';
import SearchInput from '@components/fieldsNative/SearchInput';
import Form from '@components/forms/Form';
import useMyExpert from '@hooks/useMyExpert';
import { ContentConditionSourceEnum } from '@typing/Enums';
import { ArticleContentTypeEnum, FocusAreaEnum, GetArticlesSummaryQuery } from '@typing/Generated';
import { optionsForFocusArea } from '@utils/optionUtils';

type FormData = {
  contentConditionSource?: ContentConditionSourceEnum | null;
  contentType?: ArticleContentTypeEnum | null;
  focusArea?: FocusAreaEnum | null;
  search?: string | null;
};

type ArticleSummary = GetArticlesSummaryQuery['articlesSummary'];

type Props = {
  articleSummary?: ArticleSummary;
  filters: FormData;
  onChange: (filterValues: FormData) => void;
  skipContentConditions?: boolean;
  skipQueryParams?: boolean;
};

const ArticleFilters = ({
  articleSummary,
  filters,
  onChange,
  skipContentConditions = false,
  skipQueryParams = false
}: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const contentConditionSourceParam = createEnumParam<ContentConditionSourceEnum>(
    Object.values(ContentConditionSourceEnum)
  );

  const focusAreaParam = createEnumParam<FocusAreaEnum>(Object.values(FocusAreaEnum));

  const contentTypeParam = createEnumParam<ArticleContentTypeEnum>([
    ArticleContentTypeEnum.text,
    ArticleContentTypeEnum.video
  ]);

  const [query, setQuery] = skipQueryParams
    ? [
        {
          contentConditionSource: null,
          contentType: null,
          focusArea: null,
          search: null
        },
        () => {}
      ]
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useQueryParams({
        contentConditionSource: withDefault(
          { ...contentConditionSourceParam, urlName: 'articleContentCondition' },
          filters.contentConditionSource
        ),
        contentType: withDefault({ ...contentTypeParam, urlName: 'articleContentType' }, filters.contentType),
        focusArea: withDefault({ ...focusAreaParam, urlName: 'articleFocusArea' }, filters.focusArea),
        search: withDefault({ ...StringParam, urlName: 'articleSearch' }, filters.search)
      });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange'
  });

  const contentConditionSource = form.watch('contentConditionSource');
  const focusArea = form.watch('focusArea');
  const contentType = form.watch('contentType');
  const search = form.watch('search');

  useEffect(() => {
    const values = { contentConditionSource, contentType, focusArea, search };
    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
  }, [contentConditionSource, filters, focusArea, search, onChange, setQuery, contentType]);

  const focusAreaOptions = optionsForFocusArea(intl);

  const contentConditionSourceOptions = useMemo(() => {
    const options = Object.values(ContentConditionSourceEnum).map(option => ({
      label: intl.formatMessage({ id: `models.contentCondition.source.${option}` }),
      value: option as string
    }));
    options.unshift({ label: intl.formatMessage({ id: 'dictionary.all' }), value: '' });
    return options;
  }, [intl]);

  const contentTypeOptions = useMemo(() => {
    if (articleSummary) {
      return [
        {
          label: intl.formatMessage(
            { id: 'models.article.contentTypeFilters.all' },
            { count: articleSummary.textCount + articleSummary.videoCount }
          ),
          value: null
        },
        {
          label: intl.formatMessage(
            { id: `models.article.contentTypeFilters.${ArticleContentTypeEnum.text}` },
            { count: articleSummary.textCount }
          ),
          value: ArticleContentTypeEnum.text
        },
        {
          label: intl.formatMessage(
            {
              id: `models.article.contentTypeFilters.${ArticleContentTypeEnum.video}`
            },
            { count: articleSummary.videoCount }
          ),
          value: ArticleContentTypeEnum.video
        }
      ];
    }
  }, [articleSummary, intl]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form hookForm={form} onSubmit={() => {}}>
      {contentTypeOptions && (
        <div className={skipQueryParams ? 'ion-hide' : myExpert ? styles.expertContentType : styles.contentType}>
          <RadioButtonGroup<FormData>
            control={form.control}
            error={form.formState.errors.contentType}
            name="contentType"
            options={contentTypeOptions}
          />
        </div>
      )}
      <IonGrid className={myExpert ? styles.spacing : ''}>
        <IonRow className="ion-align-items-center">
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.focusArea}
              label={intl.formatMessage({ id: 'forms.adminArticleFilters.focusArea' })}
              name="focusArea"
              options={focusAreaOptions}
            />
          </IonCol>
          {!skipContentConditions && (
            <IonCol>
              <Select<FormData>
                control={form.control}
                error={form.formState.errors.contentConditionSource}
                label={intl.formatMessage({ id: 'forms.adminArticleFilters.contentConditionSource' })}
                name="contentConditionSource"
                options={contentConditionSourceOptions}
              />
            </IonCol>
          )}
          <IonCol>
            <SearchInput
              error={form.formState.errors.search?.message}
              initialValue={defaultValues.search}
              label={intl.formatMessage({ id: 'forms.adminArticleFilters.search' })}
              name="search"
              setValue={v => form.setValue('search', v)}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default ArticleFilters;
